import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { Button, LineBreak } from '@direct-frontend/components';
import { useLazyShowProductPriceIndexConnectTooltip } from '#entities/product';
import { FeatureHintTooltip } from '#shared/components/FeatureHintTooltip';
import {
    AUTOMATIC_PRESENTATIONS,
    type AutomaticPresentationEligibility,
    useAutomaticPresentation,
} from '#shared/lib/automaticPresentation';
import { getSafeStoreInfoRequestContext } from '#shared/lib/context';
import { useNavigateWithSearchParams } from '#shared/lib/router';
import {
    SETTINGS_INVENTORY_INTEGRATION_MAP,
    SETTINGS_INVENTORY_SEARCH_PARAMS_SOURCES,
    settingsInventoryRoute,
} from '#shared/lib/routes';
import { usePersistentStorage } from '#shared/lib/storage';
import {
    PRICE_INDEX_OZON_ONBOARDING_CLOSE_LIMIT as CLOSE_LIMIT,
    PRICE_INDEX_OZON_ONBOARDING_SHOW_INTERVAL_MS,
    PRICE_INDEX_OZON_ONBOARDING_STORAGE_KEY,
} from './lib/constants';
import { parseOnboardingState } from './lib/parseOnboardingState';

const SETTINGS_OZON_URL = settingsInventoryRoute.getUrl({
    searchParams: {
        ...SETTINGS_INVENTORY_SEARCH_PARAMS_SOURCES,
        integration: SETTINGS_INVENTORY_INTEGRATION_MAP.ozon,
    },
});

export const PriceIndexOzonOnboarding: FC<PropsWithChildren> = ({ children }) => {
    const { isExternalCheckout } = getSafeStoreInfoRequestContext();
    const navigate = useNavigateWithSearchParams();
    const [storedState, setStoredState] = usePersistentStorage(PRICE_INDEX_OZON_ONBOARDING_STORAGE_KEY);
    const { closedCount, lastClosedAt } = parseOnboardingState(storedState);
    const [openedAt] = useState(Date.now);
    const canShow =
        closedCount < CLOSE_LIMIT &&
        (lastClosedAt === null || openedAt - lastClosedAt >= PRICE_INDEX_OZON_ONBOARDING_SHOW_INTERVAL_MS);

    const shouldRequest = !isExternalCheckout && canShow;

    const [fetchShowPriceIndexConnectTooltip, { data, isUninitialized, isFetching, isError }] =
        useLazyShowProductPriceIndexConnectTooltip();

    let eligibility: AutomaticPresentationEligibility = 'ineligible';

    if (shouldRequest) {
        if (isUninitialized || isFetching) {
            eligibility = 'pending';
        } else if (!isError && data?.show === true) {
            eligibility = 'eligible';
        }
    }

    const { isActive, isLeaving, startLeaving, completeLeaving } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.HIGH.priceIndexOzonOnboarding,
        eligibility,
    });

    useEffect(() => {
        if (!shouldRequest) {
            return;
        }

        void fetchShowPriceIndexConnectTooltip()
            .unwrap()
            .catch(() => undefined);
    }, [fetchShowPriceIndexConnectTooltip, shouldRequest]);

    const dismiss = () => {
        startLeaving('dismissed');
        setStoredState(JSON.stringify({ closedCount: closedCount + 1, lastClosedAt: Date.now() }));
    };

    const handleConfigure = () => {
        dismiss();
        completeLeaving();
        navigate(SETTINGS_OZON_URL);
    };

    return (
        <FeatureHintTooltip
            data-testid="PriceIndexOzonOnboarding.FeatureHint"
            isOpen={isActive}
            isClosing={isLeaving}
            onDismiss={dismiss}
            onCloseComplete={completeLeaving}
            placement="right"
            offset={110}
            flip="never"
            color="inverted"
            maxWidth="480"
            isDismissable
            title="Найдите товары с невыгодными ценами"
            caption={
                <>
                    Сравнение цен показывает, как цена товара в интернет-магазине отличается от цены на Ozon с учетом
                    скидок площадки.
                    <LineBreak />
                    Это помогает понять, почему интернет-магазин теряет часть покупателей.
                </>
            }
            buttons={
                <Button color="accent" size="s" onClick={handleConfigure}>
                    Настроить
                </Button>
            }
        >
            {children}
        </FeatureHintTooltip>
    );
};
